/**
 * Interns (PRD v3.2 section 2, day 35): the Standing Lawyers interns,
 * pre-provisioned by email with 30 days of Elite and its 90 Credits.
 *
 * Paste the cohort's addresses in any shape (one a line, or separated by
 * commas or spaces). They are tidied and checked here, then sent in batches
 * of INTERN_BATCH, one request each, so 300 addresses are eight requests. An
 * address already provisioned is left as it was and counted as such. The list
 * below shows every address, when its Elite ends, and whether the intern has
 * signed in and taken it.
 *
 * Remove takes addresses off (23 October 2026), one from its row or every
 * ticked one at once: an address can never claim Elite, and if its Elite is
 * running it ends now and the account moves to Free, with no sponsorship. The
 * account itself and the student's work stay. A selection is sent in batches of
 * INTERN_BATCH, like an added list.
 */

import { useEffect, useState } from "preact/hooks";
import type { InternGrant } from "@ls/shared";
import { ApiUnavailable, http } from "../../web/src/api";
import { INTERN_BATCH, adminErrorContract, internListResponseContract, provisionInternsResponseContract, removeInternsResponseContract } from "../../web/src/contracts.generated";
import { Button, Card, Dialog, EmptyState, PageHeader, Table } from "../../web/src/ui";

/** The addresses in pasted text, tidied, in order, once each; and what was not an address. */
export function readAddresses(text: string): { readonly emails: readonly string[]; readonly invalid: readonly string[] } {
  const emails: string[] = [];
  const invalid: string[] = [];
  for (const raw of text.split(/[\s,;]+/)) {
    const word = raw.trim().replace(/^<|>$/g, "").toLowerCase();
    if (!word) continue;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(word)) invalid.push(raw);
    else if (!emails.includes(word)) emails.push(word);
  }
  return { emails, invalid };
}

/** The addresses in batches of INTERN_BATCH. */
export function batches<T>(items: readonly T[], size: number = INTERN_BATCH): T[][] {
  const out: T[][] = [];
  for (let at = 0; at < items.length; at += size) out.push(items.slice(at, at + size));
  return out;
}

/** What Remove will do to these addresses, before it is done. */
export function removalLine(grants: readonly InternGrant[], now: Date): string {
  if (grants.length === 1) {
    const grant = grants[0] as InternGrant;
    if (grant.claimedAt === null) return `${grant.email} comes off the list and can no longer claim 30 days of Elite.`;
    if (Date.parse(grant.expiresAt) > now.getTime()) {
      return `${grant.email} comes off the list and their Elite ends now. The account moves to Free and keeps its work, with no sponsorship toward a year of Elite.`;
    }
    return `${grant.email} comes off the list. Their Elite has already ended, so their account is unchanged.`;
  }
  const running = grants.filter((grant) => grant.claimedAt !== null && Date.parse(grant.expiresAt) > now.getTime()).length;
  const waiting = grants.filter((grant) => grant.claimedAt === null).length;
  const parts = [`${grants.length} addresses come off the list.`];
  if (waiting) parts.push(`${waiting === 1 ? "One has" : `${waiting} have`} not signed in and can no longer claim 30 days of Elite.`);
  if (running) {
    parts.push(
      `${running === 1 ? "One has" : `${running} have`} Elite running: it ends now, and ${running === 1 ? "that account moves" : "those accounts move"} to Free and keep${running === 1 ? "s" : ""} ${running === 1 ? "its" : "their"} work, with no sponsorship toward a year of Elite.`,
    );
  }
  if (grants.length - waiting - running) parts.push("The rest have already finished their Elite and are unchanged.");
  return parts.join(" ");
}

function day(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

type Load = { readonly kind: "loading" } | { readonly kind: "ready"; readonly interns: readonly InternGrant[] } | { readonly kind: "failed"; readonly message: string };

export function Interns() {
  const [load, setLoad] = useState<Load>({ kind: "loading" });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ readonly bad: boolean; readonly text: string } | null>(null);

  const read = async () => {
    try {
      const reply = await http.get("/admin/interns");
      const parsed = internListResponseContract.safeParse(reply.body);
      if (reply.status === 200 && parsed.success) {
        setLoad({ kind: "ready", interns: parsed.data.interns });
        return;
      }
      const problem = adminErrorContract.safeParse(reply.body);
      setLoad({ kind: "failed", message: problem.success ? problem.data.message : "The list could not be read." });
    } catch (error) {
      setLoad({ kind: "failed", message: error instanceof ApiUnavailable ? "The server could not be reached." : "The list could not be read." });
    }
  };

  useEffect(() => {
    void read();
  }, []);

  const add = async () => {
    const { emails, invalid } = readAddresses(text);
    if (invalid.length > 0) {
      setNotice({ bad: true, text: `${invalid.length === 1 ? "This is" : "These are"} not an email address: ${invalid.slice(0, 5).join(", ")}${invalid.length > 5 ? `, and ${invalid.length - 5} more` : ""}.` });
      return;
    }
    if (emails.length === 0) {
      setNotice({ bad: true, text: "Paste at least one email address." });
      return;
    }
    setBusy(true);
    setNotice(null);
    let added = 0;
    let already = 0;
    let sent = 0;
    try {
      for (const batch of batches(emails)) {
        const reply = await http.post("/admin/interns", { emails: batch });
        const done = provisionInternsResponseContract.safeParse(reply.body);
        if (reply.status !== 200 || !done.success) {
          const problem = adminErrorContract.safeParse(reply.body);
          throw new Error(problem.success ? problem.data.message : `The server answered ${reply.status}.`);
        }
        added += done.data.added;
        already += done.data.already;
        sent += batch.length;
        setNotice({ bad: false, text: `Sent ${sent} of ${emails.length}...` });
      }
      setText("");
      setNotice({ bad: false, text: `${added} added with 30 days of Elite${already ? `; ${already} already provisioned, left as they were` : ""}.` });
    } catch (error) {
      const reason = error instanceof ApiUnavailable ? "The server could not be reached." : error instanceof Error ? error.message : "That did not go through.";
      setNotice({ bad: true, text: `${reason} ${sent} of ${emails.length} were sent (${added} added); send the rest again, and those already in are left as they were.` });
    } finally {
      setBusy(false);
      await read();
    }
  };

  const [removing, setRemoving] = useState<readonly InternGrant[] | null>(null);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());

  const pick = (email: string, on: boolean) => {
    const next = new Set(picked);
    if (on) next.add(email);
    else next.delete(email);
    setPicked(next);
  };

  const remove = async (grants: readonly InternGrant[]) => {
    setBusy(true);
    const emails = grants.map((grant) => grant.email);
    let removed = 0;
    let ended = 0;
    let sent = 0;
    try {
      for (const batch of batches(emails)) {
        const reply = await http.post("/admin/interns/remove", { emails: batch });
        const done = removeInternsResponseContract.safeParse(reply.body);
        if (reply.status !== 200 || !done.success) {
          const problem = adminErrorContract.safeParse(reply.body);
          throw new Error(problem.success ? problem.data.message : `The server answered ${reply.status}.`);
        }
        removed += done.data.removed;
        ended += done.data.eliteEnded;
        sent += batch.length;
        if (emails.length > INTERN_BATCH) setNotice({ bad: false, text: `Removed ${sent} of ${emails.length}...` });
      }
      const one = emails.length === 1 ? emails[0] : null;
      setNotice({
        bad: false,
        text:
          removed === 0
            ? `${one ?? "Those addresses"} ${one ? "was" : "were"} already off the list.`
            : `${one ? `${one} removed` : `${removed} removed`}.${ended ? ` ${ended === 1 ? "One Elite has" : `${ended} Elites have`} ended and ${ended === 1 ? "that account is" : "those accounts are"} on Free.` : ""}`,
      });
      setPicked(new Set());
    } catch (error) {
      const reason = error instanceof ApiUnavailable ? "The server could not be reached." : error instanceof Error ? error.message : "That did not go through.";
      setNotice({ bad: true, text: emails.length > 1 ? `${reason} ${sent} of ${emails.length} were sent (${removed} removed); remove the rest again.` : reason });
    } finally {
      setRemoving(null);
      setBusy(false);
      await read();
    }
  };

  const claimed = load.kind === "ready" ? load.interns.filter((grant) => grant.claimedAt !== null).length : 0;

  return (
    <div class="interns">
      <PageHeader title="Interns" />
      <Card title="Add interns">
        <form
          class="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void add();
          }}
        >
          <label class="sr" for="internEmails">
            Email addresses
          </label>
          <textarea
            id="internEmails"
            class="input interns-paste"
            rows={6}
            spellcheck={false}
            autocomplete="off"
            placeholder="One address a line"
            value={text}
            disabled={busy}
            onInput={(event) => setText((event.currentTarget as HTMLTextAreaElement).value)}
          />
          <div>
            <Button variant="primary" type="submit" disabled={busy}>
              {busy ? "Adding..." : "Add"}
            </Button>
          </div>
          {notice ? (
            <p class={notice.bad ? "notice bad" : "notice"} role={notice.bad ? "alert" : "status"} data-interns-notice>
              {notice.text}
            </p>
          ) : null}
        </form>
      </Card>
      {load.kind === "loading" ? <p class="note">Loading...</p> : null}
      {load.kind === "failed" ? (
        <p class="notice bad" role="alert">
          {load.message}
        </p>
      ) : null}
      {load.kind === "ready" && load.interns.length === 0 ? (
        <Card>
          <EmptyState title="No interns yet." line="Each address added here opens with 30 days of Elite." />
        </Card>
      ) : null}
      {load.kind === "ready" && load.interns.length > 0 ? (
        <div class="interns-bulk">
          <label class="interns-pick">
            <input
              type="checkbox"
              checked={picked.size === load.interns.length}
              disabled={busy}
              onChange={(event) => setPicked((event.currentTarget as HTMLInputElement).checked ? new Set(load.interns.map((grant) => grant.email)) : new Set())}
            />
            Select all
          </label>
          <Button size="sm" disabled={busy || picked.size === 0} onClick={() => setRemoving(load.interns.filter((grant) => picked.has(grant.email)))}>
            {picked.size === 0 ? "Remove selected" : `Remove selected (${picked.size})`}
          </Button>
        </div>
      ) : null}
      {load.kind === "ready" && load.interns.length > 0 ? (
        <Table
          caption={`${load.interns.length} ${load.interns.length === 1 ? "intern" : "interns"}, ${claimed} signed in`}
          columns={[
            { key: "pick", label: "Select" },
            { key: "email", label: "Email" },
            { key: "from", label: "From" },
            { key: "ends", label: "Elite ends" },
            { key: "claimed", label: "Signed in" },
            { key: "remove", label: "Remove" },
          ]}
          rows={load.interns.map((grant) => ({
            pick: (
              <input
                type="checkbox"
                class="interns-tick"
                aria-label={`Select ${grant.email}`}
                checked={picked.has(grant.email)}
                disabled={busy}
                onChange={(event) => pick(grant.email, (event.currentTarget as HTMLInputElement).checked)}
              />
            ),
            email: <span data-intern={grant.email}>{grant.email}</span>,
            from: day(grant.startsAt),
            ends: day(grant.expiresAt),
            claimed: grant.claimedAt ? day(grant.claimedAt) : "Not yet",
            remove: (
              <Button variant="quiet" size="sm" disabled={busy} onClick={() => setRemoving([grant])}>
                Remove<span class="sr"> {grant.email}</span>
              </Button>
            ),
          }))}
        />
      ) : null}
      <Dialog
        open={removing !== null}
        title={removing && removing.length > 1 ? `Remove ${removing.length} interns?` : "Remove this intern?"}
        onClose={() => (busy ? undefined : setRemoving(null))}
        actions={
          <>
            <Button variant="quiet" disabled={busy} onClick={() => setRemoving(null)}>
              Keep
            </Button>
            <Button variant="primary" disabled={busy} onClick={() => removing && void remove(removing)}>
              {busy ? "Removing..." : "Remove"}
            </Button>
          </>
        }
      >
        {removing ? <p class="muted">{removalLine(removing, new Date())}</p> : null}
      </Dialog>
    </div>
  );
}
